import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Building, TrendingUp, TrendingDown, Search, ArrowUpDown, BarChart3, AlertTriangle, Save, Loader2 } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ScatterChart, Scatter, ZAxis, ReferenceLine } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OUTLIER_THRESHOLD = 1000;

const fmt = (v) => `$${Math.round(v).toLocaleString()}`;

const RevenuePerLot = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('revenuePerLot');
  const [sortDir, setSortDir] = useState('desc');
  const [editingPlan, setEditingPlan] = useState(null);
  const [editLots, setEditLots] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/plan-groups`);
      setGroups(res.data.groups || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveLots = async (planNumber) => {
    const lots = parseInt(editLots, 10);
    if (isNaN(lots) || lots <= 0) return;
    setSaving(true);
    try {
      await axios.put(`${API}/plan-config/${encodeURIComponent(planNumber)}`, { occupiableLots: lots });
      setEditingPlan(null);
      setEditLots('');
      await fetchData();
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  const activeData = useMemo(() => {
    return groups
      .filter(g => (g.totalOccupiableLots || 0) > 0 && (g.revenuePerLot || 0) > 0)
      .map(g => ({
        id: g.basePlan,
        name: g.primaryPropertyName || g.basePlan,
        manager: g.primaryManager,
        lots: g.totalOccupiableLots,
        planCount: g.planCount,
        totalRevenue: g.totalRevenue,
        revenuePerLot: g.revenuePerLot || 0,
        marginPerLot: g.marginPerLot || 0,
        profit: g.profit || 0,
      }));
  }, [groups]);

  const filteredData = useMemo(() => {
    let data = activeData;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = data.filter(d =>
        d.id?.toLowerCase().includes(term) ||
        d.name?.toLowerCase().includes(term) ||
        d.manager?.toLowerCase().includes(term)
      );
    }
    data.sort((a, b) => {
      const aVal = a[sortField] || 0;
      const bVal = b[sortField] || 0;
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
    return data;
  }, [activeData, searchTerm, sortField, sortDir]);

  // Stats (excluding outliers)
  const stats = useMemo(() => {
    if (activeData.length === 0) return null;
    const nonOutliers = activeData.filter(d => d.revenuePerLot <= OUTLIER_THRESHOLD);
    const outliers = activeData.filter(d => d.revenuePerLot > OUTLIER_THRESHOLD);
    const rpls = nonOutliers.map(d => d.revenuePerLot).sort((a, b) => a - b);
    const median = rpls.length > 0 ? rpls[Math.floor(rpls.length / 2)] : 0;
    const avg = rpls.length > 0 ? rpls.reduce((s, v) => s + v, 0) / rpls.length : 0;
    // Avg gross margin per lot = total profit across non-outlier groups ÷ total lots
    const totalProfit = nonOutliers.reduce((s, d) => s + (d.profit || 0), 0);
    const totalLots = nonOutliers.reduce((s, d) => s + (d.lots || 0), 0);
    const avgMarginPerLot = totalLots > 0 ? totalProfit / totalLots : 0;
    const mpls = nonOutliers.map(d => d.marginPerLot).sort((a, b) => a - b);
    const medianMarginPerLot = mpls.length > 0 ? mpls[Math.floor(mpls.length / 2)] : 0;
    const top = nonOutliers.slice().sort((a, b) => b.revenuePerLot - a.revenuePerLot).slice(0, 10);
    const bottom = nonOutliers.slice().sort((a, b) => a.revenuePerLot - b.revenuePerLot).slice(0, 10);
    return {
      median, avg,
      avgMarginPerLot, medianMarginPerLot,
      min: rpls.length > 0 ? rpls[0] : 0,
      max: rpls.length > 0 ? rpls[rpls.length - 1] : 0,
      top, bottom,
      count: nonOutliers.length,
      outliers: outliers.sort((a, b) => b.revenuePerLot - a.revenuePerLot),
      outlierCount: outliers.length,
    };
  }, [activeData]);

  // Distribution chart data (buckets)
  const distributionData = useMemo(() => {
    if (activeData.length === 0) return [];
    const buckets = [
      { label: '$0–200', min: 0, max: 200, count: 0, revenue: 0 },
      { label: '$200–500', min: 200, max: 500, count: 0, revenue: 0 },
      { label: '$500–1k', min: 500, max: 1000, count: 0, revenue: 0 },
      { label: '$1k–2k', min: 1000, max: 2000, count: 0, revenue: 0 },
      { label: '$2k–5k', min: 2000, max: 5000, count: 0, revenue: 0 },
      { label: '$5k–10k', min: 5000, max: 10000, count: 0, revenue: 0 },
      { label: '$10k+', min: 10000, max: Infinity, count: 0, revenue: 0 },
    ];
    activeData.forEach(d => {
      const rpl = d.revenuePerLot;
      const bucket = buckets.find(b => rpl >= b.min && rpl < b.max);
      if (bucket) {
        bucket.count += 1;
        bucket.revenue += d.totalRevenue;
      }
    });
    return buckets;
  }, [activeData]);

  // Scatter data (lots vs revenue per lot)
  const scatterData = useMemo(() => {
    return activeData.map(d => ({
      x: d.lots,
      y: d.revenuePerLot,
      z: d.totalRevenue,
      name: d.name,
      id: d.id,
    }));
  }, [activeData]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-[#E5E5E5] border-t-[#171717] rounded-full animate-spin" />
      </div>
    );
  }

  const SortHeader = ({ field, children, align = 'left' }) => (
    <th
      className={`px-4 py-2.5 ${align === 'right' ? 'text-right' : 'text-left'} cursor-pointer hover:text-[#171717] transition-colors select-none`}
      onClick={() => toggleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortField === field && (
          <ArrowUpDown className="w-3 h-3 text-[#171717]" strokeWidth={2} />
        )}
      </span>
    </th>
  );

  return (
    <div className="space-y-6" data-testid="revenue-per-lot-view">
      {/* Header */}
      <div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[#171717] font-heading">Revenue per Lot</h1>
          <p className="text-sm text-[#737373] mt-1">Revenue intensity by OC plan group across the portfolio.</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
            <p className="text-xs font-medium text-[#737373] uppercase tracking-wider">Portfolio Average</p>
            <p className="text-2xl font-bold text-[#171717] mt-1">{fmt(stats.avg)}</p>
            <p className="text-[10px] text-[#A3A3A3]">revenue per lot</p>
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)]" data-testid="kpi-avg-margin-per-lot">
            <p className="text-xs font-medium text-[#737373] uppercase tracking-wider">Avg Gross Margin</p>
            <p className={`text-2xl font-bold mt-1 ${stats.avgMarginPerLot < 0 ? 'text-red-600' : 'text-[#171717]'}`}>{fmt(stats.avgMarginPerLot)}</p>
            <p className="text-[10px] text-[#A3A3A3]">per lot · median {fmt(stats.medianMarginPerLot)}</p>
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
            <p className="text-xs font-medium text-[#737373] uppercase tracking-wider">Median</p>
            <p className="text-2xl font-bold text-[#171717] mt-1">{fmt(stats.median)}</p>
            <p className="text-[10px] text-[#A3A3A3]">revenue per lot</p>
          </div>
          <div className="bg-white border border-emerald-200 rounded-[10px] p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
            <p className="text-xs font-medium text-emerald-600 uppercase tracking-wider">Highest</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">{fmt(stats.max)}</p>
            <p className="text-[10px] text-emerald-500">revenue per lot</p>
          </div>
          <div className="bg-white border border-red-200 rounded-[10px] p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
            <p className="text-xs font-medium text-red-600 uppercase tracking-wider">Lowest</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{fmt(stats.min)}</p>
            <p className="text-[10px] text-red-500">revenue per lot</p>
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
            <p className="text-xs font-medium text-[#737373] uppercase tracking-wider">OC Groups</p>
            <p className="text-2xl font-bold text-[#171717] mt-1">{stats.count}</p>
            <p className="text-[10px] text-[#A3A3A3]">excl. {stats.outlierCount} outlier{stats.outlierCount !== 1 ? 's' : ''}</p>
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribution Chart */}
        <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <div className="px-6 py-4 border-b border-[#E5E5E5]">
            <h2 className="text-base font-semibold text-[#171717] font-heading">Revenue per Lot Distribution</h2>
            <p className="text-xs text-[#737373]">How OC groups are spread across revenue-per-lot ranges</p>
          </div>
          <div className="p-6">
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={distributionData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#737373' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#737373' }} axisLine={false} tickLine={false} />
                  <Tooltip content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="bg-white border border-[#E5E5E5] rounded-[8px] p-3 shadow-lg text-sm">
                        <p className="font-semibold text-[#171717]">{label} per lot</p>
                        <p className="text-[#525252]">{payload[0].value} groups</p>
                      </div>
                    );
                  }} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={32}>
                    {distributionData.map((entry) => (
                      <Cell key={entry.label} fill={entry.label.includes('10k') ? '#10b981' : entry.label.includes('0–200') ? '#ef4444' : '#171717'} opacity={0.7} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Scatter: Lots vs Rev/Lot */}
        <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <div className="px-6 py-4 border-b border-[#E5E5E5]">
            <h2 className="text-base font-semibold text-[#171717] font-heading">Lot Count vs Revenue per Lot</h2>
            <p className="text-xs text-[#737373]">Bubble size = total revenue. Identifies outliers and pricing patterns.</p>
          </div>
          <div className="p-6">
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="x" name="Lots" tick={{ fontSize: 10, fill: '#737373' }} axisLine={false} tickLine={false} label={{ value: 'Occupiable Lots', position: 'insideBottom', offset: -2, fontSize: 10, fill: '#A3A3A3' }} />
                  <YAxis dataKey="y" name="Rev/Lot" tickFormatter={v => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: '#737373' }} axisLine={false} tickLine={false} />
                  <ZAxis dataKey="z" range={[20, 400]} />
                  <Tooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="bg-white border border-[#E5E5E5] rounded-[8px] p-3 shadow-lg text-sm">
                        <p className="font-semibold text-[#171717]">{d.name}</p>
                        <p className="text-[#525252] font-mono text-xs">{d.id}</p>
                        <p className="text-[#525252]">{d.x} lots</p>
                        <p className="text-emerald-600">{fmt(d.y)} per lot</p>
                        <p className="text-[#737373]">{fmt(d.z)} total</p>
                      </div>
                    );
                  }} />
                  <Scatter data={scatterData} fill="#171717" fillOpacity={0.5}>
                    {scatterData.map((entry) => (
                      <Cell key={entry.id} fill={entry.y > OUTLIER_THRESHOLD ? '#f59e0b' : '#171717'} fillOpacity={entry.y > OUTLIER_THRESHOLD ? 0.8 : 0.5} />
                    ))}
                  </Scatter>
                  <ReferenceLine y={OUTLIER_THRESHOLD} stroke="#f59e0b" strokeDasharray="6 3" strokeWidth={1.5} label={{ value: '$1k outlier threshold', position: 'right', fontSize: 9, fill: '#f59e0b' }} />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Top & Bottom Side by Side */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Revenue per Lot */}
          <div className="bg-white border border-emerald-200 rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
            <div className="px-6 py-4 border-b border-emerald-100 bg-emerald-50/30 rounded-t-[10px] flex items-center gap-3">
              <TrendingUp className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
              <div>
                <h2 className="text-base font-semibold text-[#171717] font-heading">Highest Revenue per Lot</h2>
                <p className="text-xs text-[#737373]">Top 10 highest-earning buildings per lot</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F8F9FA] border-b border-[#E5E5E5]">
                  <tr className="text-[10px] font-semibold uppercase tracking-wider text-[#737373]">
                    <th className="px-4 py-2.5 text-center w-8">#</th>
                    <th className="px-4 py-2.5 text-left">Property</th>
                    <th className="px-3 py-2.5 text-center">Lots</th>
                    <th className="px-3 py-2.5 text-right">Rev/Lot</th>
                    <th className="px-3 py-2.5 text-right">Total Rev</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F5F5F5]">
                  {stats.top.map((d, i) => (
                    <tr key={d.id} className="hover:bg-emerald-50/30 transition-colors">
                      <td className="px-4 py-2 text-center text-[10px] font-bold text-emerald-600">{i + 1}</td>
                      <td className="px-4 py-2">
                        <p className="text-xs font-medium text-[#171717] truncate max-w-[160px]">{d.name}</p>
                        <p className="text-[10px] text-[#A3A3A3] font-mono">{d.id}</p>
                      </td>
                      <td className="px-3 py-2 text-center text-xs text-[#525252]">{d.lots}</td>
                      <td className="px-3 py-2 text-right text-xs font-mono font-bold text-emerald-600">{fmt(d.revenuePerLot)}</td>
                      <td className="px-3 py-2 text-right text-xs font-mono text-[#525252]">{fmt(d.totalRevenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom Revenue per Lot */}
          <div className="bg-white border border-red-200 rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
            <div className="px-6 py-4 border-b border-red-100 bg-red-50/30 rounded-t-[10px] flex items-center gap-3">
              <TrendingDown className="w-4 h-4 text-red-600" strokeWidth={1.5} />
              <div>
                <h2 className="text-base font-semibold text-[#171717] font-heading">Lowest Revenue per Lot</h2>
                <p className="text-xs text-[#737373]">Bottom 10 lowest-earning buildings per lot</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F8F9FA] border-b border-[#E5E5E5]">
                  <tr className="text-[10px] font-semibold uppercase tracking-wider text-[#737373]">
                    <th className="px-4 py-2.5 text-center w-8">#</th>
                    <th className="px-4 py-2.5 text-left">Property</th>
                    <th className="px-3 py-2.5 text-center">Lots</th>
                    <th className="px-3 py-2.5 text-right">Rev/Lot</th>
                    <th className="px-3 py-2.5 text-right">Total Rev</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F5F5F5]">
                  {stats.bottom.map((d, i) => (
                    <tr key={d.id} className="hover:bg-red-50/30 transition-colors">
                      <td className="px-4 py-2 text-center text-[10px] font-bold text-red-600">{i + 1}</td>
                      <td className="px-4 py-2">
                        <p className="text-xs font-medium text-[#171717] truncate max-w-[160px]">{d.name}</p>
                        <p className="text-[10px] text-[#A3A3A3] font-mono">{d.id}</p>
                      </td>
                      <td className="px-3 py-2 text-center text-xs text-[#525252]">{d.lots}</td>
                      <td className="px-3 py-2 text-right text-xs font-mono font-bold text-red-600">{fmt(d.revenuePerLot)}</td>
                      <td className="px-3 py-2 text-right text-xs font-mono text-[#525252]">{fmt(d.totalRevenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Outlier Plans — flagged for editing */}
      {stats && stats.outlierCount > 0 && (
        <div className="bg-white border border-amber-200 rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]" data-testid="outlier-section">
          <div className="px-6 py-4 border-b border-amber-100 bg-amber-50/30 rounded-t-[10px] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-4 h-4 text-amber-600" strokeWidth={1.5} />
              <div>
                <h2 className="text-base font-semibold text-[#171717] font-heading">Outliers — Revenue per Lot &gt; $1,000</h2>
                <p className="text-xs text-[#737373]">{stats.outlierCount} plan(s) flagged. Likely incorrect lot counts — edit to correct.</p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8F9FA] border-b border-[#E5E5E5]">
                <tr className="text-[10px] font-semibold uppercase tracking-wider text-[#737373]">
                  <th className="px-4 py-2.5 text-left">Plan</th>
                  <th className="px-4 py-2.5 text-left">Property</th>
                  <th className="px-4 py-2.5 text-left">Manager</th>
                  <th className="px-3 py-2.5 text-center">Current Lots</th>
                  <th className="px-3 py-2.5 text-right">Total Revenue</th>
                  <th className="px-3 py-2.5 text-right">Rev/Lot</th>
                  <th className="px-4 py-2.5 text-center">Edit Lots</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F5F5F5]">
                {stats.outliers.map(d => (
                  <tr key={d.id} className="bg-amber-50/20 hover:bg-amber-50/40 transition-colors">
                    <td className="px-4 py-2.5 font-mono text-xs font-medium text-[#171717]">{d.id}</td>
                    <td className="px-4 py-2.5 text-xs text-[#525252] truncate max-w-[150px]">{d.name}</td>
                    <td className="px-4 py-2.5 text-xs text-[#525252]">{d.manager}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
                        {d.lots}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs font-mono text-[#171717]">{fmt(d.totalRevenue)}</td>
                    <td className="px-3 py-2.5 text-right text-xs font-mono font-bold text-amber-600">{fmt(d.revenuePerLot)}</td>
                    <td className="px-4 py-2.5 text-center">
                      {editingPlan === d.id ? (
                        <div className="flex items-center justify-center gap-1.5">
                          <input
                            type="number"
                            min="1"
                            value={editLots}
                            onChange={e => setEditLots(e.target.value)}
                            className="w-16 px-2 py-1 text-xs border border-[#E5E5E5] rounded-md text-center focus:outline-none focus:border-[#171717]"
                            autoFocus
                            onKeyDown={e => { if (e.key === 'Enter') handleSaveLots(d.id); if (e.key === 'Escape') setEditingPlan(null); }}
                          />
                          <button
                            data-testid={`save-lots-${d.id}`}
                            onClick={() => handleSaveLots(d.id)}
                            disabled={saving}
                            className="p-1 rounded bg-[#171717] text-white hover:bg-[#262626] transition-colors disabled:opacity-50"
                          >
                            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" strokeWidth={1.5} />}
                          </button>
                          <button
                            onClick={() => { setEditingPlan(null); setEditLots(''); }}
                            className="p-1 rounded text-[#A3A3A3] hover:text-[#171717] hover:bg-[#F5F5F5] transition-colors text-xs"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <button
                          data-testid={`edit-lots-${d.id}`}
                          onClick={() => { setEditingPlan(d.id); setEditLots(String(d.lots)); }}
                          className="text-[10px] px-2.5 py-1 rounded-md border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors font-medium"
                        >
                          Edit Lots
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Full Sortable Table */}
      <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-[8px] bg-[#F8F9FA] flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-[#525252]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[#171717] font-heading">All OC Groups</h2>
              <p className="text-xs text-[#737373]">Click column headers to sort</p>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A3A3A3]" strokeWidth={1.5} />
            <input
              data-testid="rpl-search"
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm border border-[#E5E5E5] rounded-[8px] bg-[#F8F9FA] text-[#171717] placeholder-[#A3A3A3] focus:outline-none focus:border-[#171717] w-56"
            />
          </div>
        </div>
        <div className="overflow-x-auto max-h-[500px]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#F8F9FA] border-b border-[#E5E5E5] text-[10px] font-semibold uppercase tracking-wider text-[#737373]">
              <tr>
                <th className="px-4 py-2.5 text-left">Group</th>
                <th className="px-4 py-2.5 text-left">Property</th>
                <th className="px-4 py-2.5 text-left">Manager</th>
                <th className="px-3 py-2.5 text-center">Plans</th>
                <SortHeader field="lots" align="right">Lots</SortHeader>
                <SortHeader field="revenuePerLot" align="right">Rev/Lot</SortHeader>
                <SortHeader field="marginPerLot" align="right">GM/Lot</SortHeader>
                <SortHeader field="totalRevenue" align="right">Total Rev</SortHeader>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F5F5F5]">
              {filteredData.slice(0, 150).map(d => {
                const isOutlier = d.revenuePerLot > OUTLIER_THRESHOLD;
                const isAboveAvg = stats && d.revenuePerLot > stats.avg;
                return (
                  <tr key={d.id} className={`transition-colors ${isOutlier ? 'bg-amber-50/30 hover:bg-amber-50/50' : 'hover:bg-[#F8F9FA]/50'}`}>
                    <td className="px-4 py-2.5 font-mono text-xs font-medium text-[#171717]">
                      {d.id}
                      {isOutlier && <AlertTriangle className="w-3 h-3 inline ml-1.5 text-amber-500" strokeWidth={2} />}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-[#525252] truncate max-w-[150px]">{d.name}</td>
                    <td className="px-4 py-2.5 text-xs text-[#525252]">{d.manager}</td>
                    <td className="px-3 py-2.5 text-center text-xs text-[#525252]">{d.planCount}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-[#525252]">{d.lots}</td>
                    <td className={`px-4 py-2.5 text-right text-xs font-mono font-medium ${isAboveAvg ? 'text-emerald-600' : 'text-red-500'}`}>
                      {fmt(d.revenuePerLot)}
                    </td>
                    <td className={`px-4 py-2.5 text-right text-xs font-mono ${d.marginPerLot < 0 ? 'text-red-500' : 'text-[#525252]'}`}>
                      {fmt(d.marginPerLot)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-xs font-mono text-[#171717]">{fmt(d.totalRevenue)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-[#E5E5E5] text-[10px] font-medium text-center uppercase tracking-wider text-[#A3A3A3] bg-[#FAFAFA] rounded-b-[10px]">
          Showing {Math.min(filteredData.length, 150)} of {filteredData.length} groups
        </div>
      </div>
    </div>
  );
};

export default RevenuePerLot;
